import { useEffect, useState } from 'react';
import { IlogisticServiceDetails, LOGISTIC_SERVICE_STATUS_NAME } from '../../../../../types/services';
import LogisticServiceOrganizationInformation from './LogisticServiceOrganizationInformation.scss/LogisticServiceOrganizationInformation';
import LogisticServiceRequesterInformation from './LogisticServiceRequesterInformation/LogisticServiceRequesterInformation';
import LogisticServiceDatesAndProgrammingDetails from './LogisticServiceDatesAndProgrammingDetails/LogisticServiceDatesAndProgrammingDetails';
import { LogisticServiceProviderDetails } from './LogisticServiceProviderDetails';
import { NudosButton } from '../../../../../components/NudosComponents';
import { IconArrowDown } from '../../../../../assets/DesignSystem/SVGComponents';
import CommentsBox from '../../../../orders/Components/CommentsBox/CommentsBox';
import { submitComment } from '../../../utils/general.utils';
import errorMessage from '../../../../../utils/errorMessage';
import LogisticServicePaymentDetail from './LogisticServicePaymentDetail/LogisticServicePaymentDetail';
import { ICommentsAndObservations } from '../../../../../types/global';
import { IResponsibleUserData } from '../../../../../types/responsibleUser.types';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../utils/displayNudosStandardNotifications';
import {
  assignLogisticResponsibleUser,
  deleteLogisticResponsibleUser,
  getLogisticResponsibleUsersList
} from '../../../../../services/responsibleUsers.service';
import { IbackofficeUserData } from '../../../../../types/configuration.types';
import useCommentsState from '../../../../../state/useCommentsState';
import { IorganizationBusinessData } from 'types/bussines';
import { getOrganizationData } from 'services/bussines';
import './LogisticServiceDetailsTab1General.scss';

const LogisticServiceDetailsTab1General = ({
  logisticServiceData,
  updateDataCallback,
  updateWithoutLoader
}: {
  logisticServiceData: IlogisticServiceDetails;
  updateDataCallback: () => Promise<void>;
  updateWithoutLoader: () => Promise<void>;
}) => {
  const [showComments, setShowComments] = useState<boolean>(true);
  const { comments, loadingComments, setLoadingComments } = useCommentsState();
  const [responsibleUsersList, setResponsibleUsersList] = useState<IResponsibleUserData[]>([]);
  const [loadingResponsibleUsersList, setLoadingResponsibleUsersList] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const [organizationInfo, setOrganizationInfo] = useState<IorganizationBusinessData | undefined>(undefined);
  const { id: logisticServiceId } = logisticServiceData;

  const showProviderInfo = [
    LOGISTIC_SERVICE_STATUS_NAME.ACTIVE,
    LOGISTIC_SERVICE_STATUS_NAME.INPROCESS,
    LOGISTIC_SERVICE_STATUS_NAME.DELIVERED
  ].includes((logisticServiceData.statusName as LOGISTIC_SERVICE_STATUS_NAME) || '');

  const handleShowComment = () => setShowComments(true);
  const handleHideComment = () => setShowComments(false);

  const getOrganizationInfo = async (): Promise<void> => {
    const organizationId = logisticServiceData.organization?.organizationId;
    if (!organizationId) return;
    try {
      const response = await getOrganizationData(Number(organizationId));
      if (response) setOrganizationInfo(response);
    } catch (error) {
      displayErrorNotification();
    }
  };
  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    if (!logisticServiceData?.id) return;

    setLoadingComments(true);
    try {
      const kamUserId = Number(organizationInfo?.assignedKam?.id);
      await submitComment({
        logisticServiceId: logisticServiceData.id || 0,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comment.comments,
        mentions: comment.mentions || [],
        commentImage: comment?.commentImage,
        ...(kamUserId && { kamUserId: kamUserId })
      });

      await updateWithoutLoader();
    } catch (error) {
      errorMessage();
    } finally {
      setLoadingComments(false);
    }
  };

  const handleGetLogisticResponsibleUsersList = async () => {
    if (!logisticServiceId) return;
    setLoadingResponsibleUsersList(true);
    try {
      const response = await getLogisticResponsibleUsersList(Number(logisticServiceId));
      if (response) setResponsibleUsersList(response);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingResponsibleUsersList(false);
    }
  };

  const handleAssignLogisticResponsible = async (user: IbackofficeUserData) => {
    if (!logisticServiceId || !user) return;
    setLoadingStates(true);
    try {
      const logisticServiceBody = {
        logisticServiceId,
        userId: user.id
      };
      const response = await assignLogisticResponsibleUser(logisticServiceBody);
      if (response) handleGetLogisticResponsibleUsersList();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingStates(false);
    }
  };

  const handleDeleteLogisticResponsible = async (responsibleId?: number, userName?: string) => {
    if (!logisticServiceId || !responsibleId) return;
    setLoadingStates(true);
    try {
      await deleteLogisticResponsibleUser(responsibleId);
      setResponsibleUsersList(responsibleUsersList.filter(user => user.id !== responsibleId));
      handleGetLogisticResponsibleUsersList();
      displaySuccessNotification({
        customJSXMessage: <div>Se eliminó al responsable {userName} de este servicio exitosamente</div>
      });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    handleGetLogisticResponsibleUsersList();
    getOrganizationInfo();
  }, [logisticServiceId]);

  return (
    <div className="LogisticServiceDetailsTab1General">
      <div className="logisticServiceDetailsTab1CompanyAndAplicantContainer">
        <LogisticServiceOrganizationInformation logisticServiceData={logisticServiceData} />
        <LogisticServiceRequesterInformation
          logisticServiceData={logisticServiceData}
          responsibleUsersList={responsibleUsersList}
          handleAssignLogisticResponsible={handleAssignLogisticResponsible}
          handleDeleteLogisticResponsible={handleDeleteLogisticResponsible}
          loadingResponsibleUsersList={loadingResponsibleUsersList}
          loadingStates={loadingStates}
        />
      </div>
      <div className="dividerLine" />

      <LogisticServicePaymentDetail
        logisticServiceData={logisticServiceData}
        updateWithLoaders={updateDataCallback}
        updateWithoutLoader={updateWithoutLoader}
      />
      <div className="dividerLine" />
      <LogisticServiceDatesAndProgrammingDetails
        logisticServiceData={logisticServiceData}
        updateDataCallback={updateDataCallback}
      />

      <LogisticServiceProviderDetails
        logisticServiceData={logisticServiceData}
        updateWithoutLoader={updateWithoutLoader}
        showProviderInfo={showProviderInfo}
      />

      <div className="whitespaceAtTheBottom w-10 h-10" />
      <NudosButton
        customClassName="buttonComments"
        buttonText="Comentarios"
        handleClick={handleShowComment}
        buttonIcon={
          <div className="contentIcon">
            <IconArrowDown />
          </div>
        }
      />
      {showComments && !!logisticServiceData.organization?.organizationId && (
        <CommentsBox
          closeComments={handleHideComment}
          comments={comments || []}
          loading={loadingComments}
          handleSubmitComments={handleCommentsSubmission}
          responsibleUsers={responsibleUsersList}
          organizationId={logisticServiceData.organization?.organizationId}
        />
      )}
    </div>
  );
};

export default LogisticServiceDetailsTab1General;
